import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../../../components/Hooks/axiosInstance";
import SetTitle from "../../../components/SetTitle";
import Footer from "../../../components/Footer/Footer";
import styles from "./KidsIsland.module.scss";
import kidsBannerImg from "./assets/kids_banner.png";
import rainbowSunImg from "./assets/rainbow_sun.png";
import planeTrailImg from "./assets/plane_trail.png";
import classroomImg from "./assets/about_classroom.jpg";
import activitiesChildImg from "./assets/activities_child.jpg";
import galleryAnnualDayImg from "./assets/gallery_annual_day.jpg";
import galleryMusicImg from "./assets/gallery_music.jpg";
import galleryCraftImg from "./assets/gallery_craft.jpg";
import galleryClassroomImg from "./assets/gallery_classroom.jpg";
import mapImg from "./assets/kids_island_map.jpg";
import teacherKidsImg from "./assets/teacher_kids.png";

const KidsIsland = () => {
	SetTitle("Kids Island International Pre School | MINE kids");
	const navigate = useNavigate();

	// Section Refs for smooth scrolling
	const inquiryFormRef = useRef(null);
	const gallerySectionRef = useRef(null);
	const visitSectionRef = useRef(null);

	const scrollToGallery = () => {
		if (gallerySectionRef.current) {
			gallerySectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	const scrollToVisit = () => {
		if (visitSectionRef.current) {
			visitSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	const [formData, setFormData] = useState({
		parentName: "",
		phoneNumber: "",
		email: "",
		childName: "",
		childAge: "",
		message: "",
	});
	const [submitted, setSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleInquireClick = (e) => {
		if (e) e.preventDefault();
		if (inquiryFormRef.current) {
			inquiryFormRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
			const firstInput = inquiryFormRef.current.querySelector("input");
			if (firstInput) {
				setTimeout(() => {
					firstInput.focus({ preventScroll: true });
				}, 600);
			}
		}
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.parentName || !formData.phoneNumber || !formData.childName) {
			return toast.warn("Parent name, phone number, and child's name are required.");
		}

		setLoading(true);
		try {
			const { data } = await axiosInstance.post("/admission/inquiry", {
				...formData,
				schoolName: "Kids Island",
			});
			toast.success(data?.msg || "Inquiry submitted successfully! We will contact you soon.");
			setSubmitted(true);
			setFormData({
				parentName: "",
				phoneNumber: "",
				email: "",
				childName: "",
				childAge: "",
				message: "",
			});
			setTimeout(() => setSubmitted(false), 4000);
		} catch (err) {
			console.error(err);
			toast.error(err?.response?.data?.msg || err?.response?.data?.message || "Failed to submit inquiry. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={styles.KidsIsland}>
			<div className={styles.Container}>
				{/* Top Hero Banner */}
				<div className={styles.HeroBanner}>
					{/* Left Content */}
					<div className={styles.HeroContent}>
						<div className={styles.WelcomeBadge}>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 2V6M12 18V22M2 12H6M18 12H22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="#174C74" strokeWidth="2.5" strokeLinecap="round"/>
							</svg>
							<span>Welcome to</span>
						</div>

						<h1 className={styles.HeroTitle}>
							Kids Island International<br />Pre school
						</h1>

						<div className={styles.SubTagWrapper}>
							<span className={styles.SubTag}>Pre school and creche</span>
							<img src={planeTrailImg} alt="Plane Trail" className={styles.PlaneTrail} />
						</div>

						<p className={styles.Description}>
							A nurturing environment where every kids minds learn, play and grow.
						</p>

						<a
							href="#inquiry-form"
							onClick={handleInquireClick}
							className={styles.InquireBtn}
						>
							<span>Inquire now</span>
							<div className={styles.ArrowCircle}>
								<FaArrowRight className={styles.ArrowIcon} />
							</div>
						</a>
					</div>

					{/* Right Illustrations & Kids */}
					<div className={styles.HeroGraphics}>
						<img src={rainbowSunImg} alt="Happy Sun & Rainbow" className={styles.RainbowSun} />
						<img src={kidsBannerImg} alt="Happy children playing at Kids Island" className={styles.KidsPhoto} />
					</div>
				</div>

				{/* About Us Section */}
				<section className={styles.AboutSection}>
					<div className={styles.ImageCard}>
						<img src={classroomImg} alt="Kids Island Classroom" className={styles.CardImg} />
					</div>

					<div className={styles.TextContent}>
						<h2 className={styles.SectionTitle}>About us</h2>
						<div className={styles.YellowUnderline}></div>
						<p className={styles.SectionDescription}>
							Kids Island International Pre School is a joyful learning space where children learn, explore and grow with confidence. As a part of the Mine Kids family, we create a nurturing environment that encourages curiosity, creativity, independence and joyful learning.
						</p>
					</div>
				</section>

				{/* Learning and Activities Section */}
				<section className={`${styles.AboutSection} ${styles.ReverseOnDesktop}`}>
					<div className={styles.TextContent}>
						<h2 className={styles.SectionTitle}>Learning and Activities</h2>
						<div className={styles.YellowUnderline}></div>
						<p className={styles.SectionDescription}>
							At Kids Island, learning goes beyond the classroom. Through play, creativity, exploration and hands-on activities, children develop new skills while having fun. From art and craft to storytelling, music, outdoor play and interactive learning, every activity is designed to encourage curiosity, imagination, communication and social development.
						</p>
					</div>

					<div className={styles.ImageCard}>
						<img src={activitiesChildImg} alt="Child learning and doing activities" className={styles.CardImg} />
					</div>
				</section>

				{/* Gallery Section - Explore our space */}
				<section className={styles.GallerySection} ref={gallerySectionRef}>
					{/* Header Content on Desktop Right / Mobile Top */}
					<div className={styles.GalleryHeader}>
						<span className={styles.GallerySubtitle}>A glimpse into our space</span>
						<div className={styles.GalleryTitleRow}>
							<h2 className={styles.GalleryTitle}>Explore our space</h2>
							<Link to="/Gallery" className={styles.SeeAllLink}>See all</Link>
						</div>
						<div className={styles.GalleryUnderline}></div>
						<p className={styles.GalleryDescription}>
							A glimpse into our joyful learning spaces, activities, and everyday moments.
						</p>
					</div>

					{/* 4 Overlapping Cards Collage */}
					<div className={styles.GalleryCollage}>
						{/* Card 1: Annual Day (Top Left) */}
						<div className={`${styles.CollageCard} ${styles.CardAnnualDay}`}>
							<img src={galleryAnnualDayImg} alt="Annual Day Celebration" className={styles.CollageImg} />
						</div>

						{/* Card 2: Music Show (Middle Overlapping) */}
						<div className={`${styles.CollageCard} ${styles.CardMusic}`}>
							<img src={galleryMusicImg} alt="Music Show" className={styles.CollageImg} />
						</div>

						{/* Card 3: Craft Activities (Bottom Left) */}
						<div className={`${styles.CollageCard} ${styles.CardCraft}`}>
							<img src={galleryCraftImg} alt="Colorful Craft Ideas" className={styles.CollageImg} />
						</div>

						{/* Card 4: Classroom Group (Bottom Right) */}
						<div className={`${styles.CollageCard} ${styles.CardClassroom}`}>
							<img src={galleryClassroomImg} alt="Everyday Learning Moments" className={styles.CollageImg} />
						</div>
					</div>
				</section>

				{/* Plan Your Visit / Reach Us Section */}
				<section className={styles.VisitSection} ref={visitSectionRef}>
					<div className={styles.VisitContent}>
						<span className={styles.VisitSubtitle}>How to reach us ?</span>
						<h2 className={styles.VisitTitle}>Plan your visit</h2>

						<div className={styles.TransitList}>
							{/* By Road */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h11l4 4h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2"/>
										<circle cx="7" cy="17" r="2"/>
										<circle cx="17" cy="17" r="2"/>
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>By road</h4>
									<p>Easily accessible via Patia / Kalarahanga Road.</p>
								</div>
							</div>

							{/* Nearest Railway */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<rect x="4" y="3" width="16" height="15" rx="2"/>
										<path d="M4 11h16"/>
										<path d="M12 3v8"/>
										<path d="m8 18-3 3"/>
										<path d="m16 18 3 3"/>
										<circle cx="8" cy="15" r="1"/>
										<circle cx="16" cy="15" r="1"/>
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>Nearest Railway station</h4>
									<p>Patia P.H. Railway Station</p>
								</div>
							</div>

							{/* Nearest Airport */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3l5.3 3.5-2 3H4.5l-1 1 3 2 2 3 1-1v-2.5l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.3c.4-.2.6-.6.5-1.1z"/>
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>Nearest Airport</h4>
									<p>Biju Patnaik International Airport</p>
								</div>
							</div>

							{/* Visiting Hours */}
							<div className={styles.TransitItem}>
								<div className={styles.TransitIcon}>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<circle cx="12" cy="12" r="10"/>
										<polyline points="12 6 12 12 16 14"/>
									</svg>
								</div>
								<div className={styles.TransitDetails}>
									<h4>Visiting hours</h4>
									<p>9:00 am - 3:00 pm</p>
								</div>
							</div>
						</div>

						<a
							href="https://maps.google.com/?q=Kids+Island+International+Pre+School+Patia+Bhubaneswar"
							target="_blank"
							rel="noopener noreferrer"
							className={styles.MapBtn}
						>
							<span>View on maps</span>
							<FaArrowRight className={styles.MapArrow} />
						</a>
					</div>

					{/* Right Map Image Card */}
					<div className={styles.MapCard}>
						<img src={mapImg} alt="Kids Island Location Map in Patia, Bhubaneswar" className={styles.MapImage} />
					</div>
				</section>

				{/* Inquiry Form & Info Section */}
				<section
					ref={inquiryFormRef}
					id="inquiry-form"
					className={styles.InquirySection}
				>
					{/* Left Inquiry Form */}
					<div className={styles.FormCard}>
						<h2 className={styles.FormTitle}>INQUIRY FORM</h2>

						<form onSubmit={handleSubmit} className={styles.FieldsStack}>
							{/* Parent Name */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>
									<span className={styles.RequiredStar}>*</span>Parent Name
								</label>
								<input
									type="text"
									name="parentName"
									value={formData.parentName}
									onChange={handleChange}
									required
									placeholder="Enter your name"
									className={styles.Input}
								/>
							</div>

							{/* Phone Number */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>
									<span className={styles.RequiredStar}>*</span>Phone Number
								</label>
								<input
									type="tel"
									name="phoneNumber"
									value={formData.phoneNumber}
									onChange={handleChange}
									required
									placeholder="Enter 10-digit number"
									className={styles.Input}
								/>
							</div>

							{/* Email */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>Email</label>
								<input
									type="email"
									name="email"
									value={formData.email}
									onChange={handleChange}
									placeholder="Enter your email"
									className={styles.Input}
								/>
							</div>

							{/* Child Name & Child Age */}
							<div className={styles.FieldsRow}>
								<div className={styles.FieldGroup}>
									<label className={styles.FieldLabel}>
										<span className={styles.RequiredStar}>*</span>Child’s Name
									</label>
									<input
										type="text"
										name="childName"
										value={formData.childName}
										onChange={handleChange}
										required
										placeholder="Child's full name"
										className={styles.Input}
									/>
								</div>

								<div className={styles.FieldGroup}>
									<label className={styles.FieldLabel}>
										<span className={styles.RequiredStar}>*</span>Child’s Age
									</label>
									<input
										type="text"
										name="childAge"
										value={formData.childAge}
										onChange={handleChange}
										required
										placeholder="e.g. 3.5 years"
										className={styles.Input}
									/>
								</div>
							</div>

							{/* Message */}
							<div className={styles.FieldGroup}>
								<label className={styles.FieldLabel}>Message</label>
								<textarea
									name="message"
									value={formData.message}
									onChange={handleChange}
									placeholder="Tell us about your requirements or questions..."
									className={styles.Textarea}
								></textarea>
							</div>

							{/* Submit Button */}
							<button type="submit" className={styles.SubmitBtn} disabled={loading}>
								{loading ? "Sending Inquiry..." : (submitted ? "Inquiry Sent!" : "Send Inquiry")}
							</button>
						</form>
					</div>

					{/* Right Info and Teacher Illustration */}
					<div className={styles.InquireInfo}>
						<div className={styles.InquireHeader}>
							<span className={styles.InquireSubtitle}>Have a question ?</span>
							<h2 className={styles.InquireTitle}>
								Inquire about Kids Island International
							</h2>
							<p className={styles.InquireDescription}>
								Interested in admissions or want to know more? Get in touch with our team and we'll be happy to help.
							</p>
						</div>

						{/* 4 Feature Cards */}
						<div className={styles.FeatureCardsList}>
							{/* Card 1: Admission Form */}
							<div
								className={`${styles.FeatureCard} ${styles.ClickableCard}`}
								onClick={() => navigate("/Admission/AdmissionForm")}>
								<div className={`${styles.CardIconCircle} ${styles.BlueIcon}`}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#174C74" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
										<path d="M6 12v5c3 3 9 3 12 0v-5"/>
									</svg>
								</div>
								<div className={styles.CardText}>
									<h3>Admission Form</h3>
									<p>Learn about our admission process and apply online.</p>
								</div>
							</div>

							{/* Card 2: Programs and music */}
							<div
								className={`${styles.FeatureCard} ${styles.ClickableCard}`}
								onClick={scrollToGallery}>
								<div className={`${styles.CardIconCircle} ${styles.PinkIcon}`}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF64AE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M9 18V5l12-2v13"/>
										<circle cx="6" cy="18" r="3"/>
										<circle cx="18" cy="16" r="3"/>
									</svg>
								</div>
								<div className={styles.CardText}>
									<h3>Programs and music</h3>
									<p>Explore our space, activities, and joyful moments.</p>
								</div>
							</div>

							{/* Card 3: Visit Information */}
							<div
								className={`${styles.FeatureCard} ${styles.ClickableCard}`}
								onClick={scrollToVisit}>
								<div className={`${styles.CardIconCircle} ${styles.GreenIcon}`}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4CD252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
										<circle cx="12" cy="10" r="3"/>
									</svg>
								</div>
								<div className={styles.CardText}>
									<h3>Visit Information</h3>
									<p>Plan a visit and explore our joyful learning environment.</p>
								</div>
							</div>

							{/* Card 4: Call directly */}
							<div className={`${styles.FeatureCard} ${styles.DashedCallCard}`}>
								<div className={`${styles.CardIconCircle} ${styles.SolidBlueIcon}`}>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="white">
										<path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.44-5.15-3.75-6.59-6.59l1.97-1.57c.28-.28.36-.67.25-1.02A11.36 11.36 0 0 1 8.56 3.99c0-.55-.45-1-1-1H4.11c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.49c0-.55-.45-1-1-1z"/>
									</svg>
								</div>
								<div className={styles.CardText}>
									<h3>Or call us directly at</h3>
									<div className={styles.PhoneRow}>
										<a href="tel:7848933200">78489 33200</a>
										<a href="tel:8013040248">80130 40248</a>
									</div>
								</div>
							</div>
						</div>

						{/* Teacher and Kids at table illustration */}
						<div className={styles.TeacherIllustrationWrapper}>
							<div className={styles.YellowGlow}></div>
							<img src={teacherKidsImg} alt="Teacher interacting with preschool children" className={styles.TeacherImg} />
						</div>
					</div>
				</section>
			</div>

			<Footer />
		</div>
	);
};

export default KidsIsland;
